import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import MapContainer from './MapContainer';
import TableView from './TableView';
import Analytics from './Analytics';
import ProjectSettings from './ProjectSettings';
import MonitoringDashboard from './components/MonitoringDashboard';
import ProjectJobWatches from './ProjectJobWatches';
import AgentChat from './AgentChat';
import AdaSidebar from './AdaSidebar';
import { AdaContextProvider } from './contexts/AdaContext';
import { FilterState } from './types/filters';

interface UploadResponse {
  message: string;
  uploadId: number;
  processed: number;
  errors: number;
  errorDetails?: any[];
  enrichmentQueued?: boolean;
  enrichmentJobId?: number;
  // Companies already present + Clay-enriched in this project, skipped so
  // we don't re-enrich (and re-charge) them.
  alreadyEnrichedSkipped?: number;
  skipped?: number;
  status?: string;
  totalRows?: number;
}

interface EnrichmentJob {
  id: number;
  total_rows: number;
  processed_rows: number;
  successful_rows: number;
  failed_rows: number;
  credits_used: number;
  status: string;
}

interface UploadProgress {
  upload_status: string;
  total_rows: number;
  processed_rows: number;
  failed_rows: number;
  isEnrichment?: boolean;
  enrichment_job?: {
    id: number;
    status: string;
    total_rows: number;
    processed_rows: number;
    successful_rows: number;
    failed_rows: number;
    credits_used: number;
  };
}

const EXPECTED_FIELDS = {
  companies: [
    { key: 'company_name', label: 'Company Name', required: true },
    { key: 'tags', label: 'Tags', required: false },
    { key: 'website', label: 'Website', required: false },
    { key: 'employee_count', label: 'Employees', required: false },
    { key: 'industry', label: 'Industry', required: false },
    { key: 'subindustry', label: 'SubIndustry', required: false },
    { key: 'specialties', label: 'Specialties', required: false },
    { key: 'description', label: 'Description', required: false },
    { key: 'url', label: 'LinkedIn', required: false },
    { key: 'founded', label: 'Founded', required: false },
    { key: 'follower_count', label: 'Followers', required: false },
    { key: 'type', label: 'Type', required: false },
    { key: 'company_size_formatted', label: 'Company Size', required: false },
    { key: 'annual_revenue', label: 'Annual Revenue', required: false },
    { key: 'business_type', label: 'Business Type', required: false },
    { key: 'business_stage', label: 'Business Stage', required: false },
    { key: 'primary_offerings', label: 'Primary Offerings', required: false },
    { key: 'revenue_streams', label: 'Revenue Streams', required: false },
    { key: 'pattern_tags', label: 'Pattern Tags', required: false },
    { key: 'scale_scope', label: 'Scale/Scope', required: false },
    { key: 'funding', label: 'Funding', required: false },
    { key: 'org_id', label: 'Org ID', required: false },
    { key: 'company_domain', label: 'Company Domain', required: false },
    { key: 'office_address', label: 'Office Address', required: true },
    { key: 'logo_url', label: 'Logo URL', required: false }
  ],
  candidates: [
    { key: 'name', label: 'Name', required: false },
    { key: 'current_position', label: 'Position', required: false },
    { key: 'current_company', label: 'Company', required: false },
    { key: 'location_full', label: 'Address', required: false },
    { key: 'li_profile_url', label: 'LinkedIn Profile URL', required: false },
    { key: 'summary', label: 'Summary', required: false },
    { key: 'headline', label: 'Headline', required: false },
    { key: 'employment_type', label: 'Employment Type', required: false },
    { key: 'company_location', label: 'Company Location', required: false },
    { key: 'phone', label: 'Phone', required: false },
    { key: 'email', label: 'Email (work)', required: false },
    { key: 'personal_email', label: 'Personal Email', required: false },
    { key: 'domain', label: 'Domain', required: false },
    { key: 'first_name', label: 'First Name', required: false },
    { key: 'last_name', label: 'Last Name', required: false },
    { key: 'working_style', label: 'Working Style', required: false },
    { key: 'all_skills', label: 'All Skills', required: false },
    { key: 'current_role_description', label: 'Current Role Description', required: false },
    { key: 'current_role_skills', label: 'Current Role Skills', required: false }
  ],
  hiring_managers: [
    { key: 'name', label: 'Name', required: false },
    { key: 'first_name', label: 'First Name', required: false },
    { key: 'last_name', label: 'Last Name', required: false },
    { key: 'job_title', label: 'Job Title', required: false },
    { key: 'org', label: 'Company Name', required: false },
    { key: 'li_profile_url', label: 'LinkedIn Profile URL', required: false },
    { key: 'li_company_url', label: 'LinkedIn Company URL', required: false },
    { key: 'summary', label: 'Summary', required: false },
    { key: 'headline', label: 'Headline', required: false },
    { key: 'location', label: 'Location', required: false },
    { key: 'email', label: 'Email', required: false },
    { key: 'phone', label: 'Phone', required: false }
  ]
};

// Simplified fields for enrichment mode - only LinkedIn URL required
const ENRICHMENT_FIELDS = {
  companies: [
    { key: 'url', label: 'LinkedIn Company URL', required: false, hint: 'Format: linkedin.com/company/company-name' },
    { key: 'company_domain', label: 'Company Domain', required: false, hint: 'e.g. example.com — used if LinkedIn URL is not available' },
    { key: 'company_name', label: 'Company Name (optional)', required: false, hint: 'Will be extracted from LinkedIn if not provided' }
  ],
  candidates: [
    { key: 'li_profile_url', label: 'LinkedIn Profile URL', required: true, hint: 'Format: linkedin.com/in/profile-name' },
    { key: 'name', label: 'Name (optional)', required: false, hint: 'Will be extracted from LinkedIn if not provided' }
  ]
};

// The original Ada agent panel, opened by the round avatar button bottom
// right. Superseded by the purple Ada tab (AdaSidebar) — hidden (Alex, 24 Oct 2026).
const SHOW_LEGACY_ADA_AGENT = false;

const RecMapperApp: React.FC = () => {
  const { user, selectedProject, clearSelectedProject, projects, loadProjects, token } = useAuth();

  // Dashboard linkage prompt: opening a project you're not linked to (not
  // starred, not a notification recipient) offers to add it to YOUR
  // dashboard. "Not now" is remembered per project so it never nags.
  const [linkBannerDismissed, setLinkBannerDismissed] = useState<Record<number, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem('mm_link_dismissed') || '{}'); } catch { return {}; }
  });
  const projectRow: any = projects?.find((p: any) => p.id === selectedProject?.id);
  const showLinkBanner = !!projectRow && projectRow.linked !== true && !linkBannerDismissed[projectRow.id];
  const dismissLinkBanner = () => {
    const next = { ...linkBannerDismissed, [projectRow.id]: true };
    setLinkBannerDismissed(next);
    try { localStorage.setItem('mm_link_dismissed', JSON.stringify(next)); } catch { /* convenience */ }
  };
  const linkProjectToDashboard = async () => {
    try {
      await fetch(`/api/projects/${projectRow.id}/star`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` },
      });
      loadProjects();
    } catch { /* refetch will settle it */ }
  };
  const navigate = useNavigate();

  const handleBackToDashboard = () => {
    clearSelectedProject();
    navigate('/projects');
  };
  const [uploadColor, setUploadColor] = useState('#3B82F6');
  const [activeView, setActiveView] = useState<'upload' | 'map' | 'table' | 'analytics' | 'monitoring' | 'jobs'>('table');
  // Alert id to focus in the monitoring dashboard (from an email deep-link
  // ?tab=monitoring&alert=<id>). Cleared once consumed.
  const [focusAlertId, setFocusAlertId] = useState<number | null>(null);
  const [agentSidebarOpen, setAgentSidebarOpen] = useState(false);

  // Deep-link handling: open the tab (and focus the alert) named in the URL,
  // e.g. from a monitoring alert email. Runs once the project is loaded.
  useEffect(() => {
    if (!selectedProject?.id) return;
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    const tabMap: Record<string, 'upload' | 'map' | 'table' | 'analytics' | 'monitoring' | 'jobs'> = {
      monitoring: 'monitoring', table: 'table', map: 'map', analytics: 'analytics', jobs: 'jobs', upload: 'upload',
    };
    if (tab && tabMap[tab]) setActiveView(tabMap[tab]);
    // …and which list in the Data Table (e.g. after adding hiring managers
    // from Contact Search: ?data=managers).
    const data = params.get('data');
    if (data === 'managers' || data === 'companies' || data === 'candidates') {
      setTableDataType(data);
      setActiveView('table');
      setTableNavNonce((n) => n + 1);
    }
    const alertId = params.get('alert');
    if (tab === 'monitoring' && alertId) setFocusAlertId(parseInt(alertId, 10) || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject?.id]);
  const [adaWorking, setAdaWorking] = useState(false);

  // Poll for Ada active tasks at app level (works even when sidebar is closed)
  useEffect(() => {
    if (!selectedProject?.id) return;
    if (!user?.adaEnabled) return; // skip polling entirely for users without Ada access
    const token = localStorage.getItem('token');
    if (!token) return;

    const checkAdaTasks = async () => {
      try {
        const res = await fetch(`/api/agent/tasks?project_id=${selectedProject.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const tasks = await res.json();
          const hasRunning = tasks.some((t: any) => t.status === 'running' || t.status === 'queued');
          setAdaWorking(hasRunning);
        }
      } catch {
        // Silent fail — don't block UI
      }
    };

    checkAdaTasks();
    const interval = setInterval(checkAdaTasks, 10000);
    return () => clearInterval(interval);
  }, [selectedProject?.id, user?.adaEnabled]);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadType, setUploadType] = useState<'companies' | 'candidates' | 'hiring_managers'>('companies');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [columnMapping, setColumnMapping] = useState<{[key: string]: string}>({});
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [showMapping, setShowMapping] = useState(false);
  const [enrichAfterUpload, setEnrichAfterUpload] = useState(false);
  const [showProjectSettings, setShowProjectSettings] = useState(false);
  const [mapRefreshKey, setMapRefreshKey] = useState(0);
  const [analyticsFilters, setAnalyticsFilters] = useState<FilterState | null>(null);
  const [enrichmentJobId, setEnrichmentJobId] = useState<number | null>(null);
  const [enrichmentJob, setEnrichmentJob] = useState<EnrichmentJob | null>(null);
  // Companies Clay never returned — surfaced on demand. Clay enriches
  // asynchronously, so we only treat these as "failed" once enrichment settles.
  const [unenriched, setUnenriched] = useState<{
    companies: { id: number; company_name: string; domain: string | null }[];
    noUrlCount: number;
    settled: boolean;
    enrichedCount: number;
    secondsSinceLastEnrichment: number | null;
  } | null>(null);
  const [unenrichedLoading, setUnenrichedLoading] = useState(false);
  const fetchUnenriched = async () => {
    if (!selectedProject?.id) return;
    setUnenrichedLoading(true);
    try {
      const res = await fetch(`/api/clay/unenriched-companies/${selectedProject.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (res.ok) {
        const d = await res.json();
        setUnenriched({ companies: d.companies || [], noUrlCount: d.noUrlCount || 0, settled: !!d.settled, enrichedCount: d.enrichedCount || 0, secondsSinceLastEnrichment: d.secondsSinceLastEnrichment });
      }
    } catch { /* non-fatal */ }
    finally { setUnenrichedLoading(false); }
  };
  const [uploadProgressId, setUploadProgressId] = useState<number | null>(null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const [uploadTag, setUploadTag] = useState('');

  // Poll upload progress (for background candidate processing)
  React.useEffect(() => {
    if (!uploadProgressId) return;

    const isEnrichmentUpload = uploadProgress?.isEnrichment ?? false;
    // Poll faster for non-enrichment uploads (they complete in ~1s)
    const pollInterval = isEnrichmentUpload ? 2000 : 500;

    const pollUploadStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/upload/status/${uploadProgressId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data: UploadProgress = await response.json();
          // Preserve the isEnrichment flag from initial state
          data.isEnrichment = isEnrichmentUpload;
          setUploadProgress(data);

          // Upload phase complete
          if (data.upload_status === 'completed' || data.upload_status === 'failed') {
            setUploadProgressId(null);
            setUploading(false);

            if (data.upload_status === 'completed') {
              setUploadResult({
                message: 'Upload complete',
                uploadId: uploadProgressId,
                processed: data.processed_rows,
                errors: data.failed_rows
              });
            }

            // If enrichment job was created, switch to enrichment polling
            if (data.enrichment_job) {
              setEnrichmentJobId(data.enrichment_job.id);
              setEnrichmentJob(data.enrichment_job as EnrichmentJob);
            }
          }
        }
      } catch (err) {
        console.error('Failed to poll upload status:', err);
      }
    };

    // Poll immediately and then at the appropriate interval
    pollUploadStatus();
    const interval = setInterval(pollUploadStatus, pollInterval);

    return () => clearInterval(interval);
  }, [uploadProgressId]);

  // Poll enrichment job status
  React.useEffect(() => {
    if (!enrichmentJobId) return;

    const pollJobStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/enrichment/jobs/${enrichmentJobId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setEnrichmentJob(data.job);

          // Stop polling if job is complete
          if (data.job.status === 'completed' || data.job.status === 'failed' || data.job.status === 'cancelled') {
            setEnrichmentJobId(null);
          }
        }
      } catch (err) {
        console.error('Failed to poll enrichment job:', err);
      }
    };

    // Poll immediately and then every 2 seconds
    pollJobStatus();
    const interval = setInterval(pollJobStatus, 2000);

    return () => clearInterval(interval);
  }, [enrichmentJobId]);

  // Callback for TableView to navigate to Analytics with filters
  const handleNavigateToAnalytics = (filterState: FilterState) => {
    setAnalyticsFilters(filterState);
    setActiveView('analytics');
  };

  // Callback for Analytics to navigate back to DataTable with filters
  const handleNavigateToDataTable = (filterState: FilterState) => {
    setAnalyticsFilters(filterState);
    setActiveView('table');
  };

  // Callback for TableView/Analytics to navigate to Map with filters
  const handleNavigateToMap = (filterState: FilterState) => {
    setAnalyticsFilters(filterState);
    setActiveView('map');
  };

  // State for company navigation from Jobs dashboard
  const [tableSearch, setTableSearch] = useState<string>('');
  const [tableDataType, setTableDataType] = useState<'companies' | 'candidates' | 'managers'>('candidates');

  // State for company filter in Jobs dashboard
  const [jobsCompanyFilter, setJobsCompanyFilter] = useState<string>('');

  // Increments on every Jobs→Company navigation. Used as part of the
  // TableView key so it remounts cleanly each time, ensuring the new
  // initialSearch is honoured (otherwise the prior TableView state can
  // swallow the prop change and the search field shows the company name
  // but no filter applies until the user types).
  const [tableNavNonce, setTableNavNonce] = useState(0);

  // Set when the user arrived at the Companies tab via the Jobs tab's
  // clickable company name. The Companies view shows a "← Back to Jobs"
  // button while this is true; clicking it returns to the Jobs view.
  const [cameFromJobs, setCameFromJobs] = useState(false);

  // Jobs → Job Spec Match handoff: a vacancy's "🎯 Match" creates a spec,
  // then this switches to the Data Table with the Job Spec Match modal
  // opened on that spec for review + scoring.
  const [jobSpecFocusId, setJobSpecFocusId] = useState<number | null>(null);
  const handleOpenJobSpecMatch = (specId: number) => {
    setJobSpecFocusId(specId);
    setTableSearch('');
    setTableDataType('candidates');
    setAnalyticsFilters(null);
    setCameFromJobs(activeView === 'jobs');
    setTableNavNonce(n => n + 1);
    setActiveView('table');
  };

  // Callback for Jobs dashboard to navigate to company in Data Table
  const handleNavigateToCompany = (companyName: string) => {
    setTableSearch(companyName);
    setTableDataType('companies');
    setAnalyticsFilters(null); // Clear any existing filters
    setCameFromJobs(activeView === 'jobs');
    setTableNavNonce(n => n + 1);
    setActiveView('table');
  };

  const handleBackToJobs = () => {
    setCameFromJobs(false);
    setTableSearch('');
    setActiveView('jobs');
  };


  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'text/csv') {
      setSelectedFile(file);
      setError(null);

      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        const firstLine = text.split('\n')[0];
        const headers = firstLine.split(',').map(h => h.trim().replace(/"/g, ''));
        setCsvHeaders(headers);
        
        const autoMapping: {[key: string]: string} = {};
        const expectedFields = EXPECTED_FIELDS[uploadType];
        
        expectedFields.forEach(field => {
          const normalizedFieldKey = field.key.toLowerCase().replace(/_/g, ' ');
          
          const matchingHeader = headers.find(header => {
            const normalizedHeader = header.toLowerCase().replace(/_/g, ' ');

            if (normalizedHeader === normalizedFieldKey) return true;
            // For 'name' field, only match exact 'name' or 'full name' — not 'first name', 'last name', 'company name', etc.
            if (field.key === 'name') {
              return normalizedHeader === 'name' || normalizedHeader === 'full name' || normalizedHeader === 'fullname';
            }
            // Work vs personal email: keep them distinct so "Personal Email"
            // doesn't get swallowed by the generic `includes('email')` below.
            if (field.key === 'email') {
              return normalizedHeader === 'email' || normalizedHeader === 'work email' || normalizedHeader === 'e-mail'
                || (normalizedHeader.includes('email') && !normalizedHeader.includes('personal'));
            }
            if (field.key === 'personal_email') {
              return normalizedHeader.includes('personal') && normalizedHeader.includes('email');
            }
            if (normalizedHeader.includes(normalizedFieldKey) || normalizedFieldKey.includes(normalizedHeader)) return true;
            if (field.key === 'url' && normalizedHeader.includes('linkedin') && normalizedHeader.includes('company')) return true;
            if (field.key === 'li_profile_url' && normalizedHeader.includes('linkedin') && !normalizedHeader.includes('company')) return true;
            if (field.key === 'li_company_url' && normalizedHeader.includes('linkedin') && normalizedHeader.includes('company')) return true;
            if (field.key === 'org' && (normalizedHeader === 'company name' || normalizedHeader === 'organisation' || normalizedHeader === 'organization' || normalizedHeader === 'company')) return true;

            return false;
          });
          
          if (matchingHeader) {
            autoMapping[field.key] = matchingHeader;
          }
        });
        
        setColumnMapping(autoMapping);
        setShowMapping(true);
      };
      reader.readAsText(file);
    } else {
      setError('Please select a CSV file');
    }
  };
  
  const handleUpload = async () => {
    if (!selectedFile || !selectedProject) return;

    setUploading(true);
    setError(null);
    setUploadResult(null);
    setUploadProgress(null);
    setUploadProgressId(null);

    const formData = new FormData();
    formData.append('csvFile', selectedFile);
    formData.append('columnMapping', JSON.stringify(columnMapping));
    formData.append('enrichAfterUpload', String(enrichAfterUpload));

    if (uploadType === 'companies') {
      formData.append('pinColor', uploadColor);
    }

    if (uploadType === 'hiring_managers' && uploadTag.trim()) {
      formData.append('tags', uploadTag.trim());
    }

    let backgroundProcessing = false;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/upload/${selectedProject.id}/${uploadType}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();

        // Background processing mode (candidates) - start polling for progress
        if (result.status === 'processing') {
          backgroundProcessing = true;
          setUploadProgressId(result.uploadId);
          setUploadProgress({
            upload_status: 'processing',
            total_rows: result.totalRows || 0,
            processed_rows: 0,
            failed_rows: 0,
            isEnrichment: enrichAfterUpload
          });
          // Keep uploading=true so the UI shows progress, polling will set it to false
          return;
        }

        // Synchronous completion (companies/hiring_managers)
        setUploadResult(result);
        setUploadTag('');

        // Start polling for enrichment progress if job was created
        if (result.enrichmentJobId) {
          setEnrichmentJobId(result.enrichmentJobId);
          setEnrichmentJob(null); // Reset any previous job state
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Upload failed');
      }
    } catch (err) {
      setError('Network error: Could not connect to server');
    } finally {
      // Only clear uploading if not in background processing mode
      if (!backgroundProcessing) {
        setUploading(false);
      }
    }
  };

  if (!selectedProject) {
    return null;
  }

  return (
    <AdaContextProvider>
    <div className="relative">
      <div className="flex">
        {/* Main content */}
        <div className={`flex-1 min-w-0 transition-all duration-300 ${agentSidebarOpen ? 'mr-[420px] max-md:mr-0' : ''}`}>
      <div>
        {/* Breadcrumb */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-sm">
            <button
              onClick={handleBackToDashboard}
              className="text-gray-500 hover:text-blue-600 transition-colors"
            >
              Projects
            </button>
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="font-semibold text-gray-900">{selectedProject.name}</span>
          </div>
          <button
            onClick={() => setShowProjectSettings(true)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title="Project Settings"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>

        {/* Dashboard linkage prompt for unlinked projects */}
        {showLinkBanner && (
          <div className="mb-4 flex items-center gap-3 rounded-xl border border-blue-100 bg-blue-50/80 px-4 py-2.5 max-md:flex-wrap">
            <span className="text-base">📌</span>
            <p className="text-xs text-blue-900 flex-1">
              Follow <b>{projectRow.name}</b>? It'll appear on your dashboard — ticker, market signals, vacancies — and pin to the top of your projects.
            </p>
            <button onClick={linkProjectToDashboard}
              className="px-3 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 whitespace-nowrap">
              ★ Follow project
            </button>
            <button onClick={dismissLinkBanner} className="text-xs text-blue-500 hover:text-blue-700 whitespace-nowrap">Not now</button>
          </div>
        )}

        <div className="flex justify-center mb-6 max-md:justify-start max-md:mb-3 max-md:-mx-3 max-md:px-3 max-md:overflow-x-auto">
          {/* Phones: one row that scrolls sideways */}
          <div className="bg-white rounded-lg p-1 shadow-sm max-md:flex max-md:flex-nowrap">
            <button
              onClick={() => {
                setTableSearch(''); // Clear any company search when clicking tab directly
                setActiveView('table');
              }}
              className={`px-6 py-2 rounded-md font-medium max-md:px-3 max-md:text-sm max-md:whitespace-nowrap max-md:flex-shrink-0 ${
                activeView === 'table'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              Data Table
            </button>
            <button
              data-tour="project-tab-map"
              onClick={() => setActiveView('map')}
              className={`px-6 py-2 rounded-md font-medium max-md:px-3 max-md:text-sm max-md:whitespace-nowrap max-md:flex-shrink-0 ${
                activeView === 'map'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              Territory Map
            </button>
            <button
              onClick={() => {
                setAnalyticsFilters(null); // Clear filters when clicking Analytics tab directly
                setActiveView('analytics');
              }}
              className={`px-6 py-2 rounded-md font-medium max-md:px-3 max-md:text-sm max-md:whitespace-nowrap max-md:flex-shrink-0 ${
                activeView === 'analytics'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              Analytics
            </button>
            <button
              onClick={() => setActiveView('monitoring')}
              className={`px-6 py-2 rounded-md font-medium max-md:px-3 max-md:text-sm max-md:whitespace-nowrap max-md:flex-shrink-0 ${
                activeView === 'monitoring'
                  ? 'bg-purple-500 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              Monitoring
            </button>
            <button
              onClick={() => {
                setJobsCompanyFilter(''); // Clear any company filter when clicking tab directly
                setActiveView('jobs');
              }}
              className={`px-6 py-2 rounded-md font-medium max-md:px-3 max-md:text-sm max-md:whitespace-nowrap max-md:flex-shrink-0 ${
                activeView === 'jobs'
                  ? 'bg-green-500 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              Jobs
            </button>
            <button
              onClick={() => setActiveView('upload')}
              className={`px-6 py-2 rounded-md font-medium max-md:px-3 max-md:text-sm max-md:whitespace-nowrap max-md:flex-shrink-0 ${
                activeView === 'upload'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              Upload Data
            </button>
          </div>
        </div>

        {activeView === 'map' ? (
          <div className="bg-white rounded-lg shadow-md p-6 max-md:p-2">
            <MapContainer
              key={mapRefreshKey}
              selectedProject={selectedProject}
              initialFilters={analyticsFilters}
              onNavigateToDataTable={handleNavigateToDataTable}
            />
          </div>
        ) : activeView === 'table' ? (
          <div className="bg-white rounded-lg shadow-md p-6 max-md:p-2">
            <TableView
              key={`tv-${selectedProject?.id}-${tableNavNonce}`}
              selectedProject={selectedProject}
              onNavigateToAnalytics={handleNavigateToAnalytics}
              onNavigateToMap={handleNavigateToMap}

              initialFilters={analyticsFilters}
              initialSearch={tableSearch}
              initialDataType={tableDataType}
              adaWorking={adaWorking}
              backToJobs={cameFromJobs ? handleBackToJobs : undefined}
              initialJobSpecId={jobSpecFocusId}
              onInitialJobSpecConsumed={() => setJobSpecFocusId(null)}
            />
          </div>
        ) : activeView === 'analytics' ? (
          <Analytics
            selectedProject={selectedProject}
            initialFilters={analyticsFilters}
            onNavigateToDataTable={handleNavigateToDataTable}
          />
        ) : activeView === 'monitoring' ? (
          <MonitoringDashboard
            projectId={selectedProject.id}
            projectName={selectedProject.name}
            focusAlertId={focusAlertId}
            onFocusAlertConsumed={() => setFocusAlertId(null)}
          />
        ) : activeView === 'jobs' ? (
          <ProjectJobWatches
            projectId={selectedProject.id}
            projectName={selectedProject.name}
            onNavigateToCompany={handleNavigateToCompany}
            onOpenJobSpecMatch={handleOpenJobSpecMatch}
          />
        ) : (
          <div className="bg-white rounded-lg shadow-md p-6">
            {/* UPLOAD FORM - keeping your existing code */}
            <h2 className="text-2xl font-semibold mb-4">CSV Upload</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Type
                </label>
                <select 
                  value={uploadType}
                  onChange={(e) => setUploadType(e.target.value as any)}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="companies">Companies</option>
                  <option value="candidates">Candidates</option>
                  <option value="hiring_managers">Hiring Managers</option>
                </select>
              </div>

              {uploadType === 'companies' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pin Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={uploadColor}
                      onChange={(e) => setUploadColor(e.target.value)}
                      className="h-12 w-20 border border-gray-300 rounded cursor-pointer"
                    />
                    <span className="text-sm text-gray-600">
                      All companies from this upload will appear as this color on the map
                    </span>
                  </div>
                </div>
              )}

              {/* Enrich after upload option - only for companies and candidates */}
              {(uploadType === 'companies' || uploadType === 'candidates') && (
                <div className="bg-purple-50 border border-purple-200 rounded-md p-4">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="enrichAfterUpload"
                      checked={enrichAfterUpload}
                      onChange={(e) => setEnrichAfterUpload(e.target.checked)}
                      className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <div>
                      <label htmlFor="enrichAfterUpload" className="text-sm font-medium text-purple-900 cursor-pointer">
                        Enrich data after upload (uses credits)
                      </label>
                      <p className="text-xs text-purple-700 mt-1">
                        {uploadType === 'companies'
                          ? 'Fetch full company data including description, employee count, location, funding, and more for each LinkedIn URL or domain.'
                          : 'Fetch full profile data including experience, skills, education, and contact info for each LinkedIn URL.'}
                      </p>
                      <p className="text-xs text-purple-600 mt-1 font-medium">
                        Cost: 1 credit per row with valid LinkedIn URL or domain
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {uploadType === 'hiring_managers' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tag hiring managers <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={uploadTag}
                    onChange={(e) => setUploadTag(e.target.value)}
                    placeholder="e.g. Upload 1, Shortlist A"
                    className="w-full p-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Separate multiple tags with commas
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CSV File
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {selectedFile && (
                <div className="bg-green-50 border border-green-200 rounded-md p-3">
                  <p className="text-green-800">
                    Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </p>
                </div>
              )}

              {showMapping && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
                  <h3 className="text-lg font-medium text-blue-900 mb-3">Map Your CSV Columns</h3>
                  <p className="text-sm text-blue-700 mb-4">
                    {enrichAfterUpload && (uploadType === 'companies' || uploadType === 'candidates')
                      ? 'Map any data you already have. We need a LinkedIn URL, domain, or company name so enrichment can find each company; everything you don’t map (e.g. office address) will be fetched automatically. Your uploaded values are preserved — enrichment only fills empty fields.'
                      : 'Map your CSV columns to the expected fields. Only map the fields you have data for. Required fields are marked with *.'}
                  </p>

                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {/* Show full mapping in both modes — when enrichAfterUpload
                        is on we just relax which fields are required. */}
                    {EXPECTED_FIELDS[uploadType].map((field: { key: string; label: string; required: boolean; hint?: string }) => (
                      <div key={field.key} className="flex items-center space-x-3">
                        <div className="w-1/3">
                          <label className="text-sm font-medium text-gray-700">
                            {field.label} {field.required && <span className="text-red-500">*</span>}
                          </label>
                          {field.hint && (
                            <p className="text-xs text-gray-500">{field.hint}</p>
                          )}
                        </div>
                        <select
                          value={columnMapping[field.key] || ''}
                          onChange={(e) => setColumnMapping({
                            ...columnMapping,
                            [field.key]: e.target.value
                          })}
                          className="w-2/3 p-2 border border-gray-300 rounded-md text-sm"
                        >
                          <option value="">-- Skip this field --</option>
                          {csvHeaders.map((header, index) => (
                            <option key={index} value={header}>{header}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 flex space-x-2">
                    <button
                      onClick={() => {
                        // Validation:
                        // - Without enrichment: respect each field's required flag (e.g. office_address).
                        // - With enrichment: drop office_address requirement (Clay will fill it),
                        //   but require at least one identifier so enrichment can find the company:
                        //   LinkedIn URL OR domain OR company name (or, for candidates,
                        //   LinkedIn profile URL).
                        if (enrichAfterUpload && (uploadType === 'companies' || uploadType === 'candidates')) {
                          if (uploadType === 'companies') {
                            const hasLink = columnMapping['url'] || columnMapping['company_domain'] || columnMapping['company_name'];
                            if (!hasLink) {
                              setError('Please map at least one of: LinkedIn URL, Company Domain, or Company Name (so enrichment can find the company).');
                              return;
                            }
                          } else if (uploadType === 'candidates') {
                            if (!columnMapping['li_profile_url']) {
                              setError('Please map LinkedIn Profile URL so enrichment can find the candidate.');
                              return;
                            }
                          }
                        } else {
                          const requiredFields = EXPECTED_FIELDS[uploadType].filter((f: { required: boolean }) => f.required);
                          const missingRequired = requiredFields.filter((f: { key: string; label: string }) => !columnMapping[f.key]);
                          if (missingRequired.length > 0) {
                            setError(`Please map required fields: ${missingRequired.map((f: { label: string }) => f.label).join(', ')}`);
                            return;
                          }
                        }

                        // For candidates and hiring managers: require either Name or First Name/Last Name
                        // Skip this check when enrichment is enabled - name will be fetched from LinkedIn
                        if (!enrichAfterUpload && (uploadType === 'candidates' || uploadType === 'hiring_managers') && !columnMapping['name'] && !columnMapping['first_name'] && !columnMapping['last_name']) {
                          setError('Please map either Name, or First Name / Last Name');
                          return;
                        }

                        setShowMapping(false);
                      }}
                      className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Continue with Upload
                    </button>
                    <button
                      onClick={() => {
                        setShowMapping(false);
                        setSelectedFile(null);
                        setCsvHeaders([]);
                        setColumnMapping({});
                      }}
                      className="px-4 py-2 text-sm bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <button
                onClick={handleUpload}
                disabled={!selectedFile || uploading || showMapping}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
              >
                {uploading && uploadProgress ? 'Processing...' : uploading ? 'Uploading...' : 'Upload CSV'}
              </button>
            </div>

            {error && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-red-800">{error}</p>
              </div>
            )}

            {/* Upload Progress Bar (background candidate processing) */}
            {uploadProgress && uploadProgress.upload_status === 'processing' && (
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-md p-4">
                {uploadProgress.isEnrichment ? (
                  <>
                    <h3 className="font-semibold text-blue-800 mb-2">
                      Processing &amp; Enriching Candidates...
                    </h3>
                    {uploadProgress.total_rows > 0 && (
                      <>
                        <div className="w-full bg-blue-200 rounded-full h-3 mb-2">
                          <div
                            className="h-3 rounded-full transition-all duration-300 bg-blue-600"
                            style={{ width: `${Math.round((uploadProgress.processed_rows / uploadProgress.total_rows) * 100)}%` }}
                          />
                        </div>
                        <div className="text-sm text-blue-700 flex justify-between">
                          <span>
                            {uploadProgress.processed_rows} of {uploadProgress.total_rows} rows processed
                            <span className="ml-2 animate-pulse">...</span>
                          </span>
                          <span>
                            {Math.round((uploadProgress.processed_rows / uploadProgress.total_rows) * 100)}%
                          </span>
                        </div>
                        {uploadProgress.failed_rows > 0 && (
                          <p className="text-xs text-red-600 mt-1">
                            {uploadProgress.failed_rows} rows with errors
                          </p>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <h3 className="font-semibold text-blue-800 mb-1">
                      Importing {uploadProgress.total_rows} candidates...
                    </h3>
                    <div className="w-full bg-blue-200 rounded-full h-2 overflow-hidden">
                      <div className="h-2 bg-blue-600 rounded-full animate-pulse" style={{ width: '100%' }} />
                    </div>
                    <p className="text-xs text-blue-600 mt-1">This usually takes a few seconds</p>
                  </>
                )}
              </div>
            )}

            {uploadResult && (
              <div className="mt-4 bg-green-50 border border-green-200 rounded-md p-4">
                <h3 className="font-semibold text-green-800 mb-2">Upload Successful!</h3>
                <div className="text-green-700">
                  <p>Processed: {uploadResult.processed} records</p>
                  {!!uploadResult.alreadyEnrichedSkipped && uploadResult.alreadyEnrichedSkipped > 0 && (
                    <p>Skipped (already enriched in this project): {uploadResult.alreadyEnrichedSkipped}</p>
                  )}
                  <p>Errors: {uploadResult.errors}</p>
                  <p>Upload ID: {uploadResult.uploadId}</p>
                </div>
              </div>
            )}

            {/* Enrichment Progress Bar */}
            {(enrichmentJobId || enrichmentJob) && (
              <div className="mt-4 bg-purple-50 border border-purple-200 rounded-md p-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-semibold text-purple-800">
                    {enrichmentJob?.status === 'processing' ? 'Enriching Data...' :
                     enrichmentJob?.status === 'completed' ? 'Enrichment Complete!' :
                     enrichmentJob?.status === 'cancelled' ? 'Enrichment Cancelled' :
                     enrichmentJob?.status === 'failed' ? 'Enrichment Failed' : 'Starting Enrichment...'}
                  </h3>
                  {enrichmentJob?.status === 'completed' && (
                    <button
                      onClick={() => {
                        setEnrichmentJob(null);
                        setEnrichmentJobId(null);
                      }}
                      className="text-purple-600 hover:text-purple-800 text-sm"
                    >
                      Dismiss
                    </button>
                  )}
                </div>

                {enrichmentJob && enrichmentJob.total_rows > 0 && (
                  <>
                    <div className="w-full bg-purple-200 rounded-full h-3 mb-2">
                      <div
                        className={`h-3 rounded-full transition-all duration-300 ${
                          enrichmentJob.status === 'completed' ? 'bg-green-500' :
                          enrichmentJob.status === 'failed' || enrichmentJob.status === 'cancelled' ? 'bg-red-500' :
                          'bg-purple-600'
                        }`}
                        style={{ width: `${Math.round((enrichmentJob.processed_rows / enrichmentJob.total_rows) * 100)}%` }}
                      />
                    </div>
                    <div className="text-sm text-purple-700 flex justify-between">
                      <span>
                        {enrichmentJob.processed_rows} of {enrichmentJob.total_rows} processed
                        {enrichmentJob.status === 'processing' && (
                          <span className="ml-2 animate-pulse">...</span>
                        )}
                      </span>
                      <span>
                        {enrichmentJob.successful_rows} successful, {enrichmentJob.failed_rows} failed
                      </span>
                    </div>
                    {enrichmentJob.credits_used > 0 && (
                      <p className="text-xs text-purple-600 mt-1">
                        Credits used: {enrichmentJob.credits_used}
                      </p>
                    )}

                    {/* Which companies didn't come back from Clay (company not found).
                        Available any time the enrichment has settled. */}
                    <div className="mt-3 pt-3 border-t border-purple-200">
                      <button
                        onClick={fetchUnenriched}
                        disabled={unenrichedLoading}
                        className="text-sm text-purple-700 hover:text-purple-900 underline disabled:opacity-50"
                      >
                        {unenrichedLoading ? 'Checking…' : (unenriched ? 'Re-check' : 'Show companies that didn’t enrich')}
                      </button>
                      {unenriched && (
                        <div className="mt-2 space-y-2">
                          {unenriched.companies.length === 0 ? (
                            <p className="text-sm text-green-700">✓ Every company with a URL enriched.</p>
                          ) : !unenriched.settled ? (
                            // Clay is still returning companies — these aren't failures yet.
                            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                              ⏳ Still enriching — Clay returns companies over a few minutes.
                              {' '}{unenriched.enrichedCount} enriched so far
                              {unenriched.secondsSinceLastEnrichment != null && `, last one ${unenriched.secondsSinceLastEnrichment}s ago`}.
                              {' '}<strong>{unenriched.companies.length}</strong> haven’t come back yet — wait a minute or two and hit <em>Re-check</em> before treating them as failed.
                            </div>
                          ) : (
                            <>
                              <div className="flex items-center justify-between mb-1">
                                <p className="text-sm font-medium text-purple-800">{unenriched.companies.length} didn’t enrich (Clay found no match):</p>
                                <button
                                  onClick={() => navigator.clipboard.writeText(unenriched.companies.map(c => `${c.company_name}\t${c.domain || ''}`).join('\n'))}
                                  className="text-xs text-purple-600 hover:text-purple-800 underline"
                                >Copy list</button>
                              </div>
                              <div className="max-h-48 overflow-y-auto rounded border border-purple-200 bg-white divide-y divide-purple-100">
                                {unenriched.companies.map(c => (
                                  <div key={c.id} className="px-3 py-1.5 text-sm flex justify-between gap-3">
                                    <span className="text-gray-800 truncate">{c.company_name}</span>
                                    <span className="text-gray-400 truncate">{c.domain}</span>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                          {unenriched.noUrlCount > 0 && (
                            <p className="text-sm text-gray-500">
                              {unenriched.noUrlCount} companies had no domain/URL to enrich — add a domain to enrich them (these were never sent to Clay).
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}

                {!enrichmentJob && (
                  <div className="flex items-center text-purple-700">
                    <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Preparing enrichment job...
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {showProjectSettings && (
          <ProjectSettings
            projectId={selectedProject.id}
            projectName={selectedProject.name}
            onClose={() => setShowProjectSettings(false)}
            onSave={() => setMapRefreshKey(prev => prev + 1)}
          />
        )}
      </div>
        </div>

        {/* AI Agent Sidebar — only rendered for users with Ada access */}
        <div
          className={`fixed top-0 right-0 h-full w-[420px] max-md:w-full bg-white border-l border-gray-200 shadow-xl z-40 transition-transform duration-300 ${
            agentSidebarOpen && user?.adaEnabled ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {agentSidebarOpen && user?.adaEnabled && (
            <div className="h-full flex flex-col">
              <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <img src="https://i.ibb.co/KjJ85nc3/ada-AI-Agent.png" alt="Ada" className="w-6 h-6 rounded-md object-cover" />
                    {adaWorking && (
                      <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-blue-500 rounded-full animate-pulse" />
                    )}
                  </div>
                  <span className="text-sm font-semibold text-gray-900">Ada</span>
                  {adaWorking ? (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700">
                      Working...
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-700">
                      Beta
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setAgentSidebarOpen(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                  title="Close Ada"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex-1 overflow-hidden">
                <AgentChat
                  projectId={selectedProject.id}
                  projectName={selectedProject.name}
                  onWorkingStateChange={setAdaWorking}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Floating Ada toggle button (the round avatar, bottom right) — retired
          24 Oct 2026: the purple Ada tab (AdaSidebar, below) does this job now.
          Set SHOW_LEGACY_ADA_AGENT to true to bring it back. */}
      {SHOW_LEGACY_ADA_AGENT && !agentSidebarOpen && user?.adaEnabled && (
        <button
          onClick={() => setAgentSidebarOpen(true)}
          className={`fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center group overflow-hidden ${
            adaWorking ? 'ring-3 ring-blue-400 ring-offset-2' : ''
          }`}
          title={adaWorking ? "Ada is working..." : "Open Ada"}
          style={adaWorking ? { animation: 'ada-pulse-ring 2s ease-in-out infinite' } : undefined}
        >
          <img src="https://i.ibb.co/KjJ85nc3/ada-AI-Agent.png" alt="Ada" className="w-full h-full object-cover" />
        </button>
      )}

      {/* Ada working animation */}
      {adaWorking && (
        <style>{`
          @keyframes ada-pulse-ring {
            0%, 100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.5), 0 4px 6px -1px rgba(0, 0, 0, 0.1); }
            50% { box-shadow: 0 0 0 8px rgba(59, 130, 246, 0), 0 4px 6px -1px rgba(0, 0, 0, 0.1); }
          }
        `}</style>
      )}
      {/* New Ada chat sidebar (Pass 2 MVP). Reads current view context from
          AdaContext, which TableView publishes to. Same ada_enabled gate as
          the legacy AgentChat above. */}
      {user?.adaEnabled && <AdaSidebar />}
    </div>
    </AdaContextProvider>
  );
};

export default RecMapperApp;
